import { isNotFound, isRedirect } from "@tanstack/react-router";
import { RpcError, RpcRedirect } from "@buildpy/api/client";
import { isLoginPath, loginHref, loginNextOf } from "@buildpy/core/modules/onboarding/rules";

import { dropRejectedSession, isNoMemberError } from "@/api/client";
import { isCustomerPath } from "@/lib/head";
import { navigateTo } from "@/lib/nav/router-ref";
import { ONBOARDING_PATH } from "@/modules/plan/copy";

// web 판 isRedirectError — 이동·없음·로그인 필요는 화면 오류가 아니라 라우터가 처리할 신호다.
export const isRedirectError = (error: unknown): boolean =>
  error instanceof RpcRedirect ||
  (error instanceof RpcError && (error.status === 401 || error.status === 404)) ||
  isRedirect(error) ||
  isNotFound(error);

// 로그인 뒤 돌아올 주소(경로+쿼리+해시) — 그 주소가 /login 이면 그 화면이 들고 있던 안전한 ?next= 를 잇는다 · 해석 못 하는 href 면 undefined.
export const returnPathOf = (href: string): string | undefined => {
  let url: URL;
  try {
    url = new URL(href, "http://localhost");
  } catch {
    return undefined;
  }
  if (isLoginPath(url.pathname)) return loginNextOf(url.searchParams.get("next")) ?? undefined;
  return `${url.pathname}${url.search}${url.hash}`;
};

// 지금 화면 주소 기준의 returnPathOf — 브라우저 밖이면 undefined.
export const hereHref = (): string | undefined => {
  if (typeof window === "undefined" || !window.location) return undefined;
  const { pathname, search, hash } = window.location;
  return returnPathOf(`${pathname}${search ?? ""}${hash ?? ""}`);
};

// 동시에 온 401 들이 세션 정리·/login 이동을 한 번만 돌게 진행 중 promise 를 공유한다(끝나면 비운다).
let leaving: Promise<void> | null = null;

// no_member(계정은 유효·멤버 없음)는 세션을 남긴 채 /onboarding 으로 — /login 은 그 세션을 다시 /onboarding 으로 보내므로 되돌이표가 없다.
const leaveToLogin = (error?: unknown): Promise<void> => {
  const target = isNoMemberError(error) ? ONBOARDING_PATH : null;
  leaving ??= dropRejectedSession(error)
    .then(() => {
      navigateTo(target ?? loginHref(hereHref()));
    })
    .finally(() => {
      leaving = null;
    });
  return leaving;
};

// 로그인 없는 화면의 첫 마디 — 고객 링크(/s/·/c/ — 간편접수·공개 페이지·소개·포트폴리오 포함) 말고 도구·초대·데모 · 실제 라우트와 맞는지는 route-tree 테스트가 본다.
export const OPEN_ROOTS = ["/tools", "/invite", "/demo"];

// 로그인 없는 화면 — 401 이어도 같은 브라우저의 멤버 세션을 지우거나 /login 으로 보내지 않는다.
export const isOpenPath = (pathname: string): boolean =>
  isCustomerPath(pathname) || OPEN_ROOTS.some((root) => pathname === root || pathname.startsWith(`${root}/`));

export const onOpenPage = (): boolean => typeof window !== "undefined" && isOpenPath(window.location.pathname);

// no_member 인데 이미 /onboarding 이면 옮길 곳이 없다 — /onboarding 은 member 액션을 부르지 않아 정상 흐름엔 없고, 생기면 호출부가 오류를 보인다.
const noMemberOnOnboarding = (error?: unknown): boolean =>
  isNoMemberError(error) && typeof window !== "undefined" && window.location?.pathname === ONBOARDING_PATH;

// 이 401 로 화면을 옮기는가 — 로그인 없는 화면·온보딩 위 no_member 면 false(호출부가 오류를 보인다) · route-error 도 같은 판정.
export const leavesOnUnauthorized = (error?: unknown): boolean => !onOpenPage() && !noMemberOnOnboarding(error);

// 401 한 벌 — 옮기지 않으면 false(화면이 오류 문구) · 회원 화면이면 no_member 는 /onboarding, 그 밖은 (토큰 무효일 때만 세션을 지우고) /login 으로 옮긴 뒤 true.
export const leaveOnUnauthorized = (error?: unknown): boolean => {
  if (!leavesOnUnauthorized(error)) return false;
  void leaveToLogin(error);
  return true;
};

// 액션 catch 머리 — RpcRedirect 는 onRedirect 가 옮겼으니 true · 401 은 leaveOnUnauthorized · 404·라우터 신호는 다시 던진다 · 진짜 오류면 false.
export const settleRedirect = (error: unknown): boolean => {
  if (error instanceof RpcRedirect) return true;
  if (error instanceof RpcError && error.status === 401) return leaveOnUnauthorized(error);
  if (isRedirectError(error)) throw error;
  return false;
};
